import { memo } from 'preact/compat';
import type { GeoProjection } from 'd3';

export type LocationPointsProps = {
	locations: MapLocation[];
	projection: GeoProjection;
	scaleFactor: number;
	hoveredIndex: number | null;
	selectedIndex: number | null;
	onLocationClick?: ( index: number ) => void;
};

/**
 * LocationPoints component. Memo'd to reduce re-renders.
 * Renders focusable SVG anchor + circle pairs for each location projected onto the map.
 * Uses SVG <a> elements for reliable keyboard focus. Click opens a detail dialog.
 *
 * @param {Object}         props                  - component props
 * @param {MapLocation[]}  props.locations        - array of map locations with lat/lng data.
 * @param {Function}       props.projection       - d3 projection function for converting lat/lng to pixel coords.
 * @param {number}         props.scaleFactor      - zoom scale factor. Divides radius and stroke to maintain consistent size.
 * @param {number|null}    props.hoveredIndex     - index of the currently hovered location.
 * @param {Function}       props.onLocationClick  - optional callback when a location point is clicked or activated via keyboard.
 * @return {*} jsx
 */
export const LocationPoints = memo( ( { locations, projection, scaleFactor = 1, hoveredIndex, selectedIndex, onLocationClick }: LocationPointsProps ) => {
	const points = locations.map( ( location, index ) => {
		const coords = projection( [ location.lng, location.lat ] );

		// Skip locations outside the projection bounds (e.g. territories).
		if ( ! coords ) {
			return null;
		}

		const isHovered = hoveredIndex === index || selectedIndex === index;
		const hasColor = !! location.color;
		const colorStyle = hasColor ? { '--str-maps-location-color': location.color } as Record< string, string > : undefined;

		return (
			<a
				key={ `loc-${ index }` }
				tabindex={ 0 }
				role="button"
				data-location-index={ index }
				aria-label={ `${ location.title }, ${ location.city }, ${ location.state }` }
				aria-describedby={ isHovered ? 'str-maps-tooltip' : undefined }
				className={ `str-maps__location-point-link ${ isHovered ? 'is-hovered' : '' }` }
				onClick={ onLocationClick ? () => onLocationClick( index ) : undefined }
				onKeyDown={ onLocationClick ? ( e: KeyboardEvent ) => {
					if ( e.key === 'Enter' || e.key === ' ' ) {
						e.preventDefault();
						onLocationClick( index );
					}
				} : undefined }
			>
				<circle
					cx={ coords[ 0 ] }
					cy={ coords[ 1 ] }
					r={ 6 / scaleFactor }
					strokeWidth={ 2 / scaleFactor }
					className={ `str-maps__location-point ${ isHovered ? 'is-hovered' : '' } ${ hasColor ? 'has-custom-color' : '' }` }
					style={ colorStyle }
				/>
			</a>
		);
	} );

	return <>{ points }</>;
} );
